import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles, Divider, Icon, Input, SearchBar, Text } from 'hoi-poi-ui';
import { isEqual } from "../../../../utils/objects";
import { getCrossID } from "../../../../utils/filters";
import { operators } from "../../operators";
import styles from "./styles";
const {
  EMPTY,
  NOT_EMPTY
} = operators;
const useStyles = createUseStyles(styles, 'Products');
const Products = _ref => {
  let {
    crossEntity,
    field,
    onChange,
    operator,
    values,
    isQuickfilter
  } = _ref;
  const [productValues, setProductValues] = useState([]);
  const [quantityValues, setQuantityValues] = useState([]);
  const classes = useStyles();
  const valuesRef = useRef({
    productValues: [],
    quantityValues: []
  });
  const isFirstLoad = useRef(true);
  const {
    id,
    inputProps,
    extraFilterProps
  } = field;
  const {
    PID,
    QID
  } = useMemo(() => ({
    PID: getCrossID(id, crossEntity),
    QID: getCrossID(extraFilterProps.id, crossEntity)
  }), [crossEntity, extraFilterProps.id, id]);
  const handleValues = useCallback(values => {
    const newProductValues = values[PID] || [];
    const newQuantityValues = Array.isArray(values[QID]) ? values[QID].map(q => ({
      value: q.value || '',
      label: q.label || undefined,
      idParent: q.idParent || undefined
    })) : !!values[QID] ? [{
      value: values[QID],
      label: undefined,
      idParent: undefined
    }] : [];
    setProductValues(newProductValues);
    setQuantityValues(newQuantityValues);
    valuesRef.current = {
      productValues: newProductValues,
      quantityValues: newQuantityValues
    };
  }, [PID, QID]);
  const handleOnChange = useCallback((updatedProductValues, updatedQuantityValues) => {
    if ([EMPTY, NOT_EMPTY].includes(operator)) {
      updatedProductValues = [];
      updatedQuantityValues = [];
    }
    onChange({
      field,
      crossEntity
    })(updatedProductValues);
    onChange({
      field: field.extraFilterProps,
      crossEntity
    })(updatedQuantityValues);
  }, [crossEntity, field, onChange, operator]);
  useEffect(() => {
    const productValues = values[PID] || [];
    const quantityValues = values[QID] || [];
    if (isFirstLoad.current) {
      handleValues(values);
      isFirstLoad.current = false;
      return;
    }
    if (isEqual(valuesRef.current.productValues, productValues) && isEqual(valuesRef.current.quantityValues, quantityValues)) {
      return;
    }
    handleValues(values);
  }, [PID, QID, handleValues, values]);
  const onChangeSearch = useCallback(products => {
    if (!products || !Array.isArray(products)) {
      handleOnChange([], []);
    } else {
      const updatedQuantityValues = products.reduce((arr, item) => {
        const currentQuantity = quantityValues.find(quantity => quantity.label === item.value);
        arr.push(currentQuantity || {
          value: '',
          label: item.value,
          idParent: undefined
        });
        return arr;
      }, []);
      handleOnChange(products, updatedQuantityValues);
    }
  }, [handleOnChange, quantityValues]);
  const onChangeQuantity = useCallback(_ref2 => {
    let {
      index,
      quantity: newQuantity,
      inner = false
    } = _ref2;
    const updatedQuantityValues = quantityValues.map((quantityObj, i) => i === index ? _objectSpread(_objectSpread({}, quantityObj), {}, {
      value: newQuantity
    }) : quantityObj);
    setQuantityValues(updatedQuantityValues);
    (!inner || !newQuantity) && handleOnChange(productValues, updatedQuantityValues);
  }, [productValues, quantityValues, handleOnChange]);
  const onDeleteRow = useCallback(index => {
    const updatedProductValues = productValues.filter((_, i) => i !== index);
    const updatedQuantityValues = quantityValues.filter((_, i) => i !== index);
    handleOnChange(updatedProductValues, updatedQuantityValues);
  }, [productValues, quantityValues, handleOnChange]);
  const searchBarProps = useMemo(() => {
    return {
      loadOptions: inputProps.loadOptions,
      placeholder: inputProps.placeholder,
      onChange: onChangeSearch,
      isReadOnly: [EMPTY, NOT_EMPTY].includes(operator),
      forceBlurOnEnter: false,
      useMenuPortal: !isQuickfilter,
      isMulti: true,
      value: productValues
    };
  }, [inputProps.loadOptions, inputProps.placeholder, isQuickfilter, onChangeSearch, operator, productValues]);
  const renderRows = useMemo(() => {
    if (productValues.length === 0) return null;
    const rows = productValues.reduce((arr, product, index) => {
      var _quantityValues$index;
      arr.push(/*#__PURE__*/React.createElement("div", {
        key: `product-${product.value}-${index}`,
        className: classes.row
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.badgeWrap
      }, /*#__PURE__*/React.createElement(Text, {
        type: "body"
      }, product.label)), /*#__PURE__*/React.createElement(Text, {
        className: classes.sep
      }, "-"), /*#__PURE__*/React.createElement("div", {
        className: classes.inputWrap
      }, /*#__PURE__*/React.createElement(Input, {
        isFullWidth: true,
        type: "integer",
        value: (_quantityValues$index = quantityValues[index]) === null || _quantityValues$index === void 0 ? void 0 : _quantityValues$index.value,
        onChange: quantity => onChangeQuantity({
          index,
          quantity,
          inner: true
        }),
        onBlur: quantity => onChangeQuantity({
          index,
          quantity
        }),
        placeholder: "0"
      })), /*#__PURE__*/React.createElement(Icon, {
        name: "delete",
        onClick: () => onDeleteRow(index)
      })));
      return arr;
    }, [/*#__PURE__*/React.createElement(Divider, {
      key: "divider",
      className: classes.divider
    })]);
    return rows;
  }, [classes.badgeWrap, classes.divider, classes.inputWrap, classes.row, classes.sep, onChangeQuantity, onDeleteRow, productValues, quantityValues]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(SearchBar, searchBarProps), renderRows);
};
export default /*#__PURE__*/memo(Products);